import { graphQLClient, reactQueryClient } from '@api/client';
import { DeviceOsEnum, useMeQuery, useSignInDeviceMutation } from '@api/GoodocAPI';
import {
  getAccessToken,
  getDeviceToken,
  getRefreshToken,
  removeAccessToken,
  removeDeviceToken,
  removeRefreshToken,
  removeRefreshTokenExpiredAt,
  saveAccessToken,
  saveDeviceToken,
  saveRefreshToken,
  saveRefreshTokenExpiredAt
} from '@utils/localStorage';
import constate from 'constate';
import { useCallback, useEffect, useMemo, useState } from 'react';
import packageJson from 'package.json';
import { getUniqueId } from '@utils/getUniqueId';
import { memoizedRotateTokenViaRefreshToken } from '@utils/tokenHandler';

interface SignInParameters {
  accessToken: string;
  refreshToken: string;
  refreshTokenExpiredAt: string;
}
const useAuthStore = () => {
  const [accessToken, setAccessToken] = useState<string | null>();
  const [deviceToken, setDeviceToken] = useState<string | null>();
  const { mutateAsync: signInDevice } = useSignInDeviceMutation(graphQLClient);
  const { data: meResponse, refetch: refetchMe } = useMeQuery(graphQLClient, undefined, {
    enabled: !!accessToken
  });

  const signIn = ({
    accessToken,
    refreshToken,
    refreshTokenExpiredAt
  }: SignInParameters) => {
    saveAccessToken(accessToken);
    setAccessToken(accessToken);
    setHeader(accessToken);
    saveRefreshToken(refreshToken);
    saveRefreshTokenExpiredAt(refreshTokenExpiredAt);
  };

  const setHeader = useCallback((token: string | null) => {
    if (!token) return;
    graphQLClient.setHeader('Authorization', `Bearer ${token}`);
  }, []);

  const isSignIn = useMemo(() => {
    return !!accessToken;
  }, [accessToken]);

  const signOut = useCallback(async () => {
    removeAccessToken();
    removeRefreshToken();
    removeRefreshTokenExpiredAt();
    setAccessToken(null);
    setHeader(null);
    await refetchMe();
  }, [refetchMe, setHeader]);

  const clearDeviceToken = () => {
    removeDeviceToken();
    setDeviceToken(null);
    setHeader(null);

    const errorsKeys = reactQueryClient
      .getQueryCache()
      .getAll()
      .filter((q) => q.state.status === 'error')
      .map((e) => e.queryKey);

    reactQueryClient.invalidateQueries(errorsKeys);

    initAuth();
  };

  const initAuth = useCallback(async () => {
    let deviceToken = getDeviceToken();
    const accessToken = getAccessToken();

    if (!deviceToken) {
      const uuid = getUniqueId();
      const result = await signInDevice({
        input: {
          os: DeviceOsEnum.Aos,
          uuid,
          deviceId: 'none',
          appVersion: packageJson.version
        }
      });

      deviceToken = result.signIn.accessToken;
      saveDeviceToken(deviceToken);
    }

    setDeviceToken(deviceToken);
    setAccessToken(accessToken);
    setHeader(accessToken ? accessToken : deviceToken);
  }, [setHeader, signInDevice]);

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  useEffect(() => {
    setInterval(() => {
      const refreshToken = getRefreshToken();
      if (refreshToken) {
        memoizedRotateTokenViaRefreshToken(refreshToken);
      }
    }, 1000 * 60 * 60); // 1시간
  }, []);

  return {
    user: meResponse?.me,
    refetchMe,
    setAccessToken,
    deviceToken,
    isSignIn,
    signIn,
    signOut,
    clearDeviceToken
  };
};

export const [AuthProvider, useAuthContext] = constate(useAuthStore);
